import React, { useState } from 'react';
import { X, Calendar, Clock, Tag, Bell, Repeat, Instagram, Youtube, Music, Trash2, Edit2, FileText, Eye } from 'lucide-react';
import type { Event } from '../../../types/calendar';
import { useCalendarStore } from '../../../stores/calendarStore';
import { useSavedIdeasStore } from '../../../stores/savedIdeasStore';
import { useContentPlanStore } from '../../../stores/contentPlanStore';
import { generateGoogleCalendarUrl } from '../../../utils/calendar';
import ContentManagerModal from './event/ContentManagerModal';
import { sanitizeHtml } from '../../../utils/sanitize';
import toast from 'react-hot-toast';

interface EventDetailsModalProps {
  event: Event;
  onClose: () => void;
  onEdit: () => void;
}

export default function EventDetailsModal({ event, onClose, onEdit }: EventDetailsModalProps) {
  const [showContentManager, setShowContentManager] = useState(false);
  const { deleteEvent } = useCalendarStore();
  const { savedIdeas } = useSavedIdeasStore();
  const { plans } = useContentPlanStore();

  const linkedIdea = event.contentIdeaId ? 
    savedIdeas.find(idea => idea.title === event.contentIdeaId) : null;
  const linkedPlan = event.contentPlanId ? 
    plans.find(plan => plan.id === event.contentPlanId) : null;

  const getPlatformIcon = () => {
    switch (event.platform) {
      case 'instagram':
        return <Instagram className="h-5 w-5 text-pink-600" />;
      case 'youtube':
        return <Youtube className="h-5 w-5 text-red-600" />;
      case 'tiktok':
        return <Music className="h-5 w-5 text-black" />;
    }
  };

  const handleDelete = () => {
    if (window.confirm('Tem certeza que deseja excluir este evento?')) {
      deleteEvent(event.id);
      onClose();
    }
  };

  const handleAddToGoogleCalendar = () => {
    const url = generateGoogleCalendarUrl(event);
    window.open(url, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-lg w-full mx-4 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-3">
            {getPlatformIcon()}
            <h2 className="text-xl font-semibold text-gray-900">{event.title}</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onEdit}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
              title="Editar"
            >
              <Edit2 className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
              title="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Date and Time */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-gray-700">
              <Calendar className="h-5 w-5 text-gray-400" />
              <span>
                {new Date(event.start).toLocaleDateString('pt-BR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}
              </span>
            </div>
            <div className="flex items-center space-x-2 text-gray-700">
              <Clock className="h-5 w-5 text-gray-400" />
              <span>
                {new Date(event.start).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })}
                {' - '}
                {new Date(event.end).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })}
              </span>
            </div>
          </div>

          {/* Google Calendar Button */}
          <button 
            onClick={handleAddToGoogleCalendar}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Calendar className="h-5 w-5" />
            <span>Adicionar ao Google Agenda</span>
          </button>

          {/* Details */}
          <div className="space-y-4">
            {/* Status and Priority */}
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1 rounded-full text-sm ${
                event.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                event.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                'bg-green-100 text-green-800'
              }`}>
                {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm ${
                event.priority === 'high' ? 'bg-red-100 text-red-800' :
                event.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                {event.priority.charAt(0).toUpperCase() + event.priority.slice(1)}
              </span>
            </div>

            {/* Linked Content */}
            {(linkedIdea || linkedPlan) && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">Conteúdo Vinculado</h3>
                  {linkedPlan && (
                    <button
                      onClick={() => setShowContentManager(true)}
                      className="p-2 text-gray-600 hover:text-gray-900 rounded-full transition-colors"
                      title="Ver plano completo"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                  )}
                </div>
                {linkedIdea && (
                  <div className="flex items-start space-x-3">
                    <Tag className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">{linkedIdea.title}</p>
                      <p className="text-sm text-gray-600">{linkedIdea.description}</p>
                    </div>
                  </div>
                )}
                {linkedPlan && (
                  <div className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">{linkedPlan.title}</p>
                      <p className="text-sm text-gray-500">
                        Criado em {new Date(linkedPlan.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recurrence */}
            {event.recurrence && (
              <div className="flex items-center space-x-2 text-gray-700">
                <Repeat className="h-5 w-5 text-gray-400" />
                <span>
                  Repete {event.recurrence.frequency === 'daily' ? 'diariamente' : 
                         event.recurrence.frequency === 'weekly' ? 'semanalmente' : 'mensalmente'}
                  {event.recurrence.interval > 1 ? ` a cada ${event.recurrence.interval} ${
                    event.recurrence.frequency === 'daily' ? 'dias' :
                    event.recurrence.frequency === 'weekly' ? 'semanas' : 'meses'
                  }` : ''}
                </span>
              </div>
            )}

            {/* Reminders */}
            {event.reminders && event.reminders.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-gray-700">
                  <Bell className="h-5 w-5 text-gray-400" />
                  <span>Lembretes</span>
                </div>
                <div className="ml-7 space-y-1">
                  {event.reminders.map((minutes, index) => (
                    <div key={index} className="text-sm text-gray-600">
                      {minutes === '1440' ? '1 dia' : `${minutes} minutos`} antes
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between items-center bg-gray-50">
          <button
            onClick={handleDelete}
            className="text-red-600 hover:text-red-700 font-medium"
          >
            Excluir
          </button>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Fechar
            </button>
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Editar
            </button>
          </div>
        </div>
      </div>

      {/* Content Manager Modal */}
      {showContentManager && linkedPlan && (
        <ContentManagerModal
          event={event}
          onClose={() => setShowContentManager(false)}
          onEdit={onEdit}
        />
      )}
    </div>
  );
}